import React, { useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Checkbox,
  Alert,
  IconButton,
  Divider,
} from '@mui/material';
import { Print, Delete, QrCode, SelectAll } from '@mui/icons-material';
import { QREntity } from './QRGenerator';

interface BatchPrintProps {
  printQueue: QREntity[];
  selectedItems: string[];
  onToggleSelect: (id: string) => void;
  onSelectAll: () => void;
  onClearQueue: () => void;
  onRemoveItem: (id: string) => void;
}

const BatchPrint: React.FC<BatchPrintProps> = ({
  printQueue,
  selectedItems,
  onToggleSelect,
  onSelectAll,
  onClearQueue,
  onRemoveItem,
}) => {
  const printAreaRef = useRef<HTMLDivElement>(null);

  const selectedEntities = printQueue.filter((e) => selectedItems.includes(e.id));

  const handlePrint = () => {
    if (selectedEntities.length === 0) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const labelHtml = selectedEntities
      .map(
        (entity) => `
        <div class="label">
          <div class="qr-placeholder">QR</div>
          <div class="label-info">
            <div class="label-type">${entity.type.replace('_', ' ').toUpperCase()}</div>
            <div class="label-code">${entity.code}</div>
            <div class="label-name">${entity.name}</div>
            ${
              entity.metadata
                ? Object.entries(entity.metadata)
                    .map(([k, v]) => `<div class="label-meta">${k}: ${v}</div>`)
                    .join('')
                : ''
            }
          </div>
        </div>
      `
      )
      .join('');

    printWindow.document.write(`
      <html>
        <head>
          <title>Batch QR Label Print</title>
          <style>
            body { margin: 0; padding: 16px; font-family: Arial, sans-serif; }
            .labels-container { display: flex; flex-wrap: wrap; gap: 8px; }
            .label {
              border: 1px solid #000;
              padding: 8px;
              width: 200px;
              height: 80px;
              display: flex;
              align-items: center;
              gap: 8px;
              box-sizing: border-box;
              page-break-inside: avoid;
            }
            .qr-placeholder {
              width: 60px; height: 60px; min-width: 60px;
              border: 1px dashed #999;
              display: flex; align-items: center; justify-content: center;
              font-size: 8px; color: #999;
            }
            .label-info { flex: 1; overflow: hidden; }
            .label-type { font-size: 8px; color: #555; text-transform: uppercase; }
            .label-code { font-weight: bold; font-size: 11px; }
            .label-name { font-size: 9px; }
            .label-meta { font-size: 8px; color: #666; }
            @media print {
              .labels-container { display: flex; flex-wrap: wrap; }
            }
          </style>
        </head>
        <body>
          <div class="labels-container">
            ${labelHtml}
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const allSelected = printQueue.length > 0 && selectedItems.length === printQueue.length;
  const someSelected = selectedItems.length > 0 && selectedItems.length < printQueue.length;

  return (
    <Box>
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
            <Typography variant="h6">
              Print Queue ({printQueue.length} items)
            </Typography>
            <Box display="flex" gap={1}>
              <Button
                startIcon={<SelectAll />}
                onClick={onSelectAll}
                size="small"
                variant="outlined"
                disabled={printQueue.length === 0}
              >
                {allSelected ? 'Deselect All' : 'Select All'}
              </Button>
              <Button
                startIcon={<Print />}
                onClick={handlePrint}
                variant="contained"
                size="small"
                disabled={selectedItems.length === 0}
              >
                Print Selected ({selectedItems.length})
              </Button>
              <Button
                startIcon={<Delete />}
                onClick={onClearQueue}
                color="error"
                size="small"
                variant="outlined"
                disabled={printQueue.length === 0}
              >
                Clear Queue
              </Button>
            </Box>
          </Box>

          {printQueue.length === 0 ? (
            <Alert severity="info" icon={<QrCode />}>
              No items in print queue. Generate QR codes and add them to the queue from the Generate tab.
            </Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={allSelected}
                        indeterminate={someSelected}
                        onChange={onSelectAll}
                      />
                    </TableCell>
                    <TableCell>Code</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Details</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {printQueue.map((entity) => (
                    <TableRow
                      key={entity.id}
                      selected={selectedItems.includes(entity.id)}
                      hover
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedItems.includes(entity.id)}
                          onChange={() => onToggleSelect(entity.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {entity.code}
                        </Typography>
                      </TableCell>
                      <TableCell>{entity.name}</TableCell>
                      <TableCell>
                        <Chip
                          label={entity.type.replace(/_/g, ' ').toUpperCase()}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {entity.metadata
                          ? Object.entries(entity.metadata)
                              .slice(0, 2)
                              .map(([k, v]) => (
                                <Typography key={k} variant="caption" display="block" color="textSecondary">
                                  {k}: {v}
                                </Typography>
                              ))
                          : '—'}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => onRemoveItem(entity.id)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default BatchPrint;
